import Plugin from '../Plugin'

/**
 * PlaytimeEnforcement - Client-side playtime limit handling
 * 
 * Handles server-side playtime enforcement events:
 * - playtime_warning: Shows countdown warnings before limit reached
 * - playtime_limit_reached: Shows final message and disconnects player
 * - playtime_status: Receives remaining playtime for HUD display
 * 
 * COPPA Compliance: Parents can set daily playtime limits for child accounts.
 * This client plugin displays appropriate UI for these restrictions.
 */
export default class PlaytimeEnforcement extends Plugin {

    constructor(network) {
        super(network)
        this.events = {
            'playtime_warning': this.onPlaytimeWarning.bind(this),
            'playtime_limit_reached': this.onPlaytimeLimitReached.bind(this),
            'playtime_status': this.onPlaytimeStatus.bind(this)
        }
        
        // Track remaining playtime for HUD display
        this.remainingMinutes = null
        this.playtimeLimit = null
        
        // Track warning state to avoid duplicate popups
        this.lastWarningMinutes = null
    }

    /**
     * Handle playtime warning from server
     * Shows a non-blocking notification with remaining time
     */
    onPlaytimeWarning(args) {
        const remainingMinutes = args.remainingMinutes
        const message = args.message || `You have ${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''} of playtime remaining.`
        
        // Update tracking
        this.remainingMinutes = remainingMinutes
        
        // Avoid showing duplicate warnings for the same minute threshold
        if (this.lastWarningMinutes === remainingMinutes) {
            return
        }
        this.lastWarningMinutes = remainingMinutes
        
        // Determine warning level based on remaining time
        let title = '⏰ Playtime Reminder'
        if (remainingMinutes <= 1) {
            title = '⚠️ Playtime Almost Up!'
        } else if (remainingMinutes <= 5) {
            title = '⏰ Playtime Warning'
        }
        
        // Show the warning using the system prompt
        try {
            if (this.interface?.prompt) {
                this.interface.prompt.showWindow(
                    `${title}\n\n${message}`,
                    'single'
                )
            }
        } catch (error) {
            console.error('[PlaytimeEnforcement] Failed to show warning:', error)
        }
    }

    /**
     * Handle playtime limit reached from server
     * Shows a final message before disconnection
     */
    onPlaytimeLimitReached(args) {
        const message = args.message || "Your playtime limit for today has been reached.\n\nAsk your parent for more time, or come back tomorrow!"
        
        // Reset tracking
        this.remainingMinutes = 0
        this.lastWarningMinutes = null
        
        try {
            if (this.interface?.prompt) {
                this.interface.prompt.showError(
                    `⏰ Time's Up!\n\n${message}`,
                    'Goodbye',
                    () => {
                        // Server will disconnect us, but close the prompt cleanly
                        this.interface.prompt.error.close()
                        // Optionally return to start screen
                        try {
                            this.scene.start('Start')
                        } catch (e) {
                            // May already be disconnected
                            window.location.reload()
                        }
                    }
                )
            }
        } catch (error) {
            console.error('[PlaytimeEnforcement] Failed to show limit reached message:', error)
            // Fallback - just reload after a delay
            setTimeout(() => window.location.reload(), 3000)
        }
    }

    /**
     * Handle playtime status update from server
     * Used to update HUD display of remaining time
     */
    onPlaytimeStatus(args) {
        this.remainingMinutes = args.remainingMinutes
        this.playtimeLimit = args.playtimeLimit
        
        // Emit event for any HUD components listening
        try {
            this.network.events.emit('playtime_updated', {
                remainingMinutes: this.remainingMinutes,
                playtimeLimit: this.playtimeLimit
            })
        } catch (error) {
            // Silent failure - HUD update is non-critical
        }
    }

    /**
     * Format remaining time for display
     * @returns {string|null} Formatted time string or null if no limit
     */
    getFormattedRemainingTime() {
        if (this.remainingMinutes === null) {
            return null
        }
        
        const hours = Math.floor(this.remainingMinutes / 60)
        const minutes = this.remainingMinutes % 60
        
        if (hours > 0) {
            return `${hours}h ${minutes}m`
        }
        return `${minutes}m`
    }

}
